import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { peek } from '../data/routeData';
import { clubsKey, loadClubs } from '../data/routeLoaders';
import PageShell from '../components/chrome/PageShell';
import LoadingSpinner from '../components/LoadingSpinner';
import clubService from '../services/clubService';
import portalService from '../services/portalService';
import useScrollRestore from '../hooks/useScrollRestore';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight from '../hooks/useTopBarHeight';
import { RING } from '../components/wall/wallTokens';
import { TownSquareRoom, PortalNoirRoom } from '../components/clubs/PinnedRooms';
import ClubGrid from '../components/clubs/ClubGrid';
import RingMark from '../components/clubs/RingMark';
import InviteCodePanel from '../components/clubs/InviteCodePanel';
import ClosedClubs from '../components/clubs/ClosedClubs';
import { columnsAt, seedRatios } from '../components/clubs/clubMasonry';
import '../components/clubs/clubsMotion.css';

/**
 * Clubs — "your rooms" (clubs redesign handoff, docs/plans/2026-09-clubs-redesign).
 *
 * A white intro, the two pinned rooms (City Portal and Portal Noir), a sticky MY CLUBS /
 * DISCOVER switch, then the club grid: borderless white cards on the canvas, laid out as
 * masonry, each keeping its cover's own orientation. Join by invite code closes the page.
 *
 * The cards carry no border at rest and wear the Portal ring as one on hover — the same
 * vocabulary as the events wall and City Portal, so the three browse surfaces read as one app.
 */

const LOCATION_KEY = 'pr.userLocation';

/** The town in the kicker, from wherever the wall last put the viewer. */
function storedTown() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    const label = raw ? JSON.parse(raw)?.label : null;
    // Labels arrive as "Penticton, BC" — the kicker wants the town alone.
    return label ? String(label).split(',')[0].trim().toUpperCase() : null;
  } catch {
    return null;
  }
}

export default function ClubsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const phone = useMediaQuery('(max-width: 639px)');
  const narrow = useMediaQuery('(max-width: 979px)');
  // Hover latches on a tap, so the lift / zoom / shine belong to pointers only (handoff §3).
  const hoverable = useMediaQuery('(hover: hover) and (pointer: fine)');
  const cols = columnsAt(phone ? 400 : narrow ? 800 : 1280);
  const gap = phone ? 14 : 20;
  const padX = phone ? 16 : 28;
  const headerH = useTopBarHeight();

  // Whether to say MEMBER on the Portal Noir door. `/portal/state` is the authority —
  // "accepted" is the only state that counts as being in Portal Noir.
  const [isPortalMember, setIsPortalMember] = useState(false);

  // The tab rides the URL, as it does on People. Coming back from a club has to land on the tab
  // you left — restoring a scroll position onto MY CLUBS when you were half-way down DISCOVER is
  // worse than not restoring at all — and it makes a tab linkable and refresh-safe on the way.
  const activeTab = searchParams.get('tab') === 'discover' ? 'discover' : 'my';
  // Discover's list depends on the search, so the search has to come back too, or the offset is
  // restored onto a different list of clubs.
  const search = searchParams.get('q') || '';

  // One position per tab+search, so each list keeps its own place.
  useScrollRestore(`clubs|${activeTab}|${search}`);

  const [myClubs, setMyClubs] = useState(() => peek(clubsKey('my')) || []);
  const [publicClubs, setPublicClubs] = useState(() => { const data = peek(clubsKey('discover', search)); return data?.items || data || []; });
  const [inviteCode, setInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(() => !peek(clubsKey(activeTab, search)));
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState(null);
  const [joinError, setJoinError] = useState(null);
  // Covers are measured once per URL and kept for the session, so switching tabs and coming back
  // doesn't re-flow the grid while the same images load again.
  const [ratios, setRatios] = useState(seedRatios);
  // A tab's count is only the truth once that tab's list has answered. Before then it's null and
  // the pill shows no number — "DISCOVER 0" on a page you haven't opened yet reads as "there are
  // none", which is the opposite of what it means.
  const [counts, setCounts] = useState(() => ({
    my: peek(clubsKey('my'))?.length ?? null,
    discover: (() => { const d = peek(clubsKey('discover', search)); const items = d?.items || d; return items ? items.length : null; })(),
  }));
  // What this visit did: id → 'joined' | 'requested', laid over what the server last said.
  const [joined, setJoined] = useState({});
  const [busyId, setBusyId] = useState(null);

  const town = useMemo(storedTown, []);

  const onRatio = useCallback((url, ratio) => {
    setRatios((prev) => (prev[url] === ratio ? prev : { ...prev, [url]: ratio }));
  }, []);

  // Your rooms load on both tabs, not just MY CLUBS: the intro's YOUR CLUBS count, the CLUB
  // PORTAL LIVE pill and the City Portal room's LIVE count are all about your clubs, and they
  // sit above the switch on either tab. One cached request rather than four empty pills.
  useEffect(() => {
    let cancelled = false;
    async function fetchMyClubs() {
      setIsLoading(!peek(clubsKey('my')));
      setError(null);
      try {
        const data = await loadClubs('my');
        if (!cancelled) { setMyClubs(data || []); setCounts((prev) => ({ ...prev, my: (data || []).length })); }
      }
      catch (err) { if (!cancelled) setError(err.message); }
      if (!cancelled) setIsLoading(false);
    }
    fetchMyClubs();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await portalService.getState();
      if (!cancelled && r.success) setIsPortalMember(r.data?.state === 'accepted');
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTab !== 'discover') return undefined;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setIsLoading(!peek(clubsKey('discover', search)));
      setError(null);
      try {
        const data = await loadClubs('discover', search);
        const items = data?.items || data || [];
        if (!cancelled) { setPublicClubs(items); setCounts((prev) => ({ ...prev, discover: items.length })); }
      }
      catch (err) { if (!cancelled) setError(err.message); }
      if (!cancelled) setIsLoading(false);
    }, search ? 400 : 0);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [activeTab, search]);

  const handleTabChange = useCallback((tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'my') next.delete('tab'); else next.set('tab', tab);
      // A tab's search doesn't belong to the other tab.
      next.delete('q');
      return next;
    });
    setError(null);
  }, [setSearchParams]);

  // replace, not push: typing a query must not put a history entry behind every keystroke for
  // the user to back out through.
  const setSearch = useCallback((value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('q', value); else next.delete('q');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleJoinByInviteCode = async () => {
    const code = inviteCode.trim();
    if (code.length < 4) return;
    setIsJoining(true);
    setJoinError(null);
    const result = await clubService.joinByInviteCode(code);
    if (result.success) {
      const clubId = result.data?.clubId || result.data?.id;
      if (clubId) {
        navigate(`/clubs/${clubId}`);
      } else {
        const myResult = await clubService.getMyClubs();
        if (myResult.success) setMyClubs(myResult.data || []);
        handleTabChange('my');
        setInviteCode('');
      }
    } else {
      setJoinError(result.error || 'Invalid invite code');
    }
    setIsJoining(false);
  };

  /** JOIN / REQUEST from a card. A paid club can't be joined here — its checkout is its page. */
  const handleJoin = async (club) => {
    if (club.membershipMode && club.membershipMode !== 'free') { navigate(`/clubs/${club.id}`); return; }
    setBusyId(club.id);
    const result = await clubService.joinClub(club.id);
    setBusyId(null);
    if (!result.success) {
      if ((result.error || '').includes('CLUB_REQUIRES_PAYMENT')) navigate(`/clubs/${club.id}`);
      return;
    }
    setJoined((prev) => ({ ...prev, [club.id]: result.data?.pending ? 'requested' : 'joined' }));
  };

  const rawClubs = activeTab === 'my' ? myClubs : publicClubs;
  const clubs = useMemo(
    () => rawClubs.map((club) => (joined[club.id] ? { ...club, viewerStatus: joined[club.id] } : club)),
    [rawClubs, joined],
  );
  const livePortals = myClubs.filter((c) => c.hasLivePortal).length;
  const liveClub = myClubs.find((c) => c.hasLivePortal) || null;

  const tabs = [
    { key: 'my', label: 'MY CLUBS', n: counts.my },
    { key: 'discover', label: 'DISCOVER', n: counts.discover },
  ];
  const countLabel = activeTab === 'my'
    ? [`${counts.my ?? 0} CLUB${counts.my === 1 ? '' : 'S'}`, livePortals ? `${livePortals} PORTAL LIVE` : null].filter(Boolean).join(' · ')
    : (town ? `NEAR ${town}` : `${publicClubs.length} CLUB${publicClubs.length === 1 ? '' : 'S'}`);

  return (
    <PageShell active="clubs" bare>
      <div style={{ paddingBottom: narrow ? 80 : 0 }}>
        {/* INTRO */}
        <section style={{ background: '#fff' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: phone ? '24px 16px 20px' : '40px 28px 32px', boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.24em', color: '#6B7280' }}>
                {['YOUR ROOMS', town].filter(Boolean).join(' · ')}
              </span>
              <h1 style={{ fontSize: phone ? 40 : 'clamp(52px,6vw,80px)', fontWeight: 800, letterSpacing: '0.02em', lineHeight: 0.9, margin: 0 }}>CLUBS</h1>
              <span style={{ fontSize: 14 }}>Your rooms.</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {livePortals > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px 6px 6px', borderRadius: 999, background: '#000', color: '#fff' }} data-testid="intro-live-pill">
                  <RingMark size={32} spin={5} direction="ccw" ripples={[0, 1.8]} inset="6%">
                    {liveClub?.avatarUrl
                      ? <img src={liveClub.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 12, fontWeight: 800, color: '#000' }}>{liveClub?.name?.charAt(0)?.toUpperCase() || '?'}</span>}
                  </RingMark>
                  <span style={{ fontSize: 16, fontWeight: 800 }}>{livePortals}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em' }}>CLUB PORTAL LIVE</span>
                </span>
              )}
              {/* Below 980 this row would wrap over the section rule, so the second pill waits. */}
              {!narrow && counts.my !== null && (
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 16px', borderRadius: 999, background: '#F2F2F2' }}>
                  <span style={{ fontSize: 16, fontWeight: 800 }}>{counts.my ?? 0}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em' }}>YOUR CLUBS</span>
                </span>
              )}
            </div>
          </div>
        </section>

        {/* PINNED ROOMS */}
        <section style={{ borderTop: '2px solid #000' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: phone ? 16 : '24px 28px', boxSizing: 'border-box', display: 'grid', gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))', gap }}>
            <TownSquareRoom onEnter={() => navigate('/square')} live={livePortals} phone={phone} />
            <PortalNoirRoom onEnter={() => navigate(isPortalMember ? '/noir/home' : '/noir')} isMember={isPortalMember} phone={phone} />
          </div>
        </section>

        {/* TABS — sticky under the top bar */}
        <div style={{ position: 'sticky', top: headerH, zIndex: 20, background: 'rgba(245,245,245,.96)', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: `10px ${padX}px`, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: phone ? 'repeat(2,minmax(0,1fr))' : 'repeat(2,auto)', gap: 6, flex: phone ? 1 : '0 0 auto' }}>
              {tabs.map((tab) => {
                const on = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTabChange(tab.key)}
                    aria-pressed={on}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      height: 40,
                      padding: '0 20px',
                      border: '2px solid transparent',
                      borderRadius: 999,
                      // Black with the ring as its border — the app's one "this is selected".
                      background: on ? `linear-gradient(#000,#000) padding-box, ${RING} border-box` : '#fff',
                      color: on ? '#fff' : '#000',
                      fontFamily: 'inherit',
                      fontSize: phone ? 11 : 12,
                      fontWeight: 700,
                      letterSpacing: '0.16em',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color .2s, color .2s',
                    }}
                  >
                    {tab.label}
                    {tab.n !== null && <span style={{ fontSize: 10, opacity: 0.55 }}>{tab.n}</span>}
                  </button>
                );
              })}
            </div>
            {!narrow && (
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: '#6B7280' }}>{countLabel}</span>
            )}
          </div>
        </div>

        <main style={{ maxWidth: 1280, margin: '0 auto', padding: phone ? '20px 16px 40px' : '28px 28px 64px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* DISCOVER keeps its search: the tab's list, and the place it restores to, are both
              keyed on it. The top bar's search is the whole app; this one narrows this grid. */}
          {activeTab === 'discover' && (
            <input
              type="text"
              placeholder="SEARCH CLUBS"
              aria-label="Search clubs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ height: 44, borderRadius: 999, border: 'none', background: '#fff', padding: '0 20px', fontFamily: 'inherit', fontSize: 13, letterSpacing: '0.1em', color: '#000', outline: 'none' }}
            />
          )}

          {isLoading ? (
            <div style={{ padding: '64px 0', textAlign: 'center' }}><LoadingSpinner label="LOADING..." /></div>
          ) : error ? (
            <div style={{ background: '#fff', padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', color: '#FF00AA' }}>ERROR</div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#4B5563' }}>{error}</div>
            </div>
          ) : (
            <>
              {clubs.length === 0 && activeTab === 'discover' && (
                <div style={{ background: '#fff', padding: 32, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em' }}>NO CLUBS FOUND</div>
                  <div style={{ marginTop: 8, fontSize: 13, color: '#4B5563' }}>Try a different search, or start your own club.</div>
                </div>
              )}
              {/* MY CLUBS with nothing in it isn't empty — START YOUR OWN CLUB is the first card
                  either way, so the grid always has something to say. */}
              <ClubGrid
                clubs={clubs}
                cols={cols}
                gap={gap}
                ratios={ratios}
                onRatio={onRatio}
                onOpen={(club) => navigate(`/clubs/${club.id}`)}
                onJoin={handleJoin}
                busyId={busyId}
                hoverable={hoverable}
                showStart={activeTab === 'my'}
                onStart={() => navigate('/clubs/create')}
              />
            </>
          )}

          {activeTab === 'my' && <ClosedClubs onRestored={(club) => navigate(`/clubs/${club.id}`)} phone={phone} />}

          <InviteCodePanel
            code={inviteCode}
            onCode={(value) => { setInviteCode(value); setJoinError(null); }}
            onJoin={handleJoinByInviteCode}
            busy={isJoining}
            error={joinError}
            phone={phone}
          />
        </main>
      </div>
    </PageShell>
  );
}
